
import store from "@/store"
import useJwt from '@/auth/jwt/useJwt'

import { get } from "lodash"
import { canNavigate, checkLoginTokenStatus } from '@/libs/acl/routeProtection'
import { MUTATE_LOGIN_STATUS, MUTATE_SETTINGS, MUTATE_USER_DATA, MUTATE_PHONE_VERIFICATION_STATUS } from "@/store/config/mutation-keys"

export async function beforePasswordReset(to, _from, next) {
  const { token: passwordResetToken } = to.query;
  if (!passwordResetToken) {
    store.commit(`auth/${MUTATE_LOGIN_STATUS}`, false)
    return next({ name: "auth-login" });
  }

  try {
    useJwt.mainService.auth.setPasswordResetToken(passwordResetToken);
    await useJwt.mainService.auth.confirmTokenValidity("password_reset_token");
    return next();
  } catch (error) {
    return next({ name: "auth-forgot-password", query: { error: "Password reset token has expired" } });
  }
}

export async function beforeEnteringAuthenticatedRoute(to, from, next) {
  const userIsLoggedIn = store.getters[`auth/isLoggedIn`];

  if (to.name !== from.name) {
    let authorized = userIsLoggedIn;
    let user = store.getters[`auth/userData`];
    let settings = store.getters[`auth/settings`];

    if (!(userIsLoggedIn && user && settings)) {
      console.log("[check token]")
      const authCheckResponse = await checkLoginTokenStatus();

      user = authCheckResponse.user;
      settings = authCheckResponse.settings;
      authorized = authCheckResponse.authorized;
      store.commit(`auth/${MUTATE_LOGIN_STATUS}`, true);
      store.commit(`auth/${MUTATE_USER_DATA}`, user);
      store.commit(`auth/${MUTATE_SETTINGS}`, settings);
    }

    if (!authorized) {
      console.log("[not authorized]")
      return next({ name: 'auth-login' });
    }

    /**
     * this validation is for routes that only allow access if certain prop is not available.
     */
    const supportedRoutesWithPropValidations = [
      "auth-verify-phone",
      "onboarding"
    ];

    if (supportedRoutesWithPropValidations.includes(to.name)) {
      if (!authorized) {
        return next({ name: 'auth-login' });
      }

      const phoneNumberVerified = get(user, 'meta.phone_verified', false);
      const requirePhoneVerification = get(settings, 'security_settings.require_phone_verification', false);

      // redirects if phone number already verified
      if (phoneNumberVerified && requirePhoneVerification && to.name === 'auth-verify-phone') {
        return next("/");
      }

      const { has_completed_onboarding: onboardingCompleted = false } = user.member_profile || {};
      // redirect if already on-boarded
      if (onboardingCompleted && to.name === "onboarding") {
        return next("/");
      }
    }

    const { has_completed_onboarding: onboardingCompleted = false } = user.member_profile || {}

    if (!onboardingCompleted) {
      if (to.name !== 'onboarding') {
        console.log("[to onboarding]")
        return next({ name: "onboarding" });
      }

      return next();
    }

    const phoneVerificationStatus = get(user, 'meta.phone_verified', false);
    const requirePhoneVerification = get(settings, 'security_settings.require_phone_verification', false);
    store.commit(`auth/${MUTATE_PHONE_VERIFICATION_STATUS}`, phoneVerificationStatus);

    if (!phoneVerificationStatus && requirePhoneVerification) {
      return next({ name: 'auth-verify-phone' });
    }

    const requireMFA = get(settings, 'security_settings.require_mfa', false);
    if (!user.mfa_conf && requireMFA && to.name !== "security-mfa") {
      console.log("[to security mfa]")
      sessionStorage.setItem("last_page_accessed", to.path)
      return next({ name: "security-mfa" })
    }

    const daysBeforePasswordExpire = user.days_before_password_expire;

    if (daysBeforePasswordExpire <= 0 && to.name !== 'security-password-change') {
      console.log("[to change password]")
      return next({ name: 'security-password-change' })
    }
    /**
     * redirect user to dashboard.
     */
    if (to.name === "app-root") {
      console.log("[to home]")
      return next({ name: "home" })
    }

    // check is user has access to route.
    if (canNavigate(to, user.user_type)) {
      // console.log("[to proceed]")
      return next();
    }

    return next({ name: 'misc-not-authorized' });
  }

  return next();
}
